/**
 * @graphql cc
 */
import gql from 'graphql-tag';
import React, { useContext } from 'react';
import { useIntl, FormattedMessage } from 'react-intl-next';
// We have deprecated unstated. Please use react-sweet-state instead
// eslint-disable-next-line no-restricted-imports
import { Subscribe } from 'unstated';
import { useQuery } from '@apollo/react-hooks';

import { DropdownItem as AkDropdownItem } from '@atlaskit/dropdown-menu';
import AkTooltip from '@atlaskit/tooltip/Tooltip';
import AkButton from '@atlaskit/button/custom-theme-button';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { DialogsStateContainer } from '@confluence/dialogs';
import { ErrorDisplay } from '@confluence/error-boundary';
import { MovePageDialog } from '@confluence/page-tree/entry-points/MovePageDialog';
import { MoveBlogModalLoader } from '@confluence/move-blog-modal';
import {
	MOVE_PAGE_DIALOG_LOAD_EXPERIENCE,
	ExperienceTrackerContext,
} from '@confluence/experience-tracker';

import type {
	EditorMovePageButtonQuery as EditorMovePageButtonQuery$data,
	EditorMovePageButtonQueryVariables as EditorMovePageButtonQuery$variables,
} from './__types__/EditorMovePageButtonQuery';
import type {
	EditorMovePageButtonComponentWithDialogsProps,
	EditorMovePageButtonComponentProps,
} from './types';
import { i18n } from './i18n';

const EditorMovePageButtonComponentWithDialogs: React.FC<
	EditorMovePageButtonComponentWithDialogsProps
> = ({
	spaceKey,
	contentId,
	appearance = 'dropdownItem',
	analyticsSource,
	dialogs,
	retrieveEditorTitleAndContent,
	isUnpublishedDraft,
}) => {
	const intl = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { data, loading, error } = useQuery<
		EditorMovePageButtonQuery$data,
		EditorMovePageButtonQuery$variables
	>(
		gql`
			query EditorMovePageButtonQuery($contentId: ID!) {
				content(id: $contentId, status: ["current", "draft"]) {
					nodes {
						id
						type
						status
						operations {
							operation
							targetType
						}
					}
				}
			}
		`,
		{
			variables: {
				contentId,
			},
			displayName: 'EditorMovePageButtonQuery',
		},
	);
	const experienceTracker = useContext(ExperienceTrackerContext);

	if (loading) return null;

	const content = data?.content?.nodes?.[0];
	const contentType = content?.type || '';
	const operations = data?.content?.nodes?.[0]?.operations || [];
	const canMoveContent =
		isUnpublishedDraft ||
		contentType === 'page' ||
		!!operations.find((op) => op?.targetType === contentType && op?.operation === 'delete');

	const openMoveModal = () => {
		if (contentType === 'page') {
			experienceTracker.start({
				name: MOVE_PAGE_DIALOG_LOAD_EXPERIENCE,
			});
			dialogs.showModal(MovePageDialog, {
				contentId,
				spaceKey,
				isUnpublishedDraft,
				isMovingDraftVersion: true,
				referrer: 'editor-move-page-button',
			});

			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'openEditorMovePageDialog',
					source: analyticsSource,
					objectId: contentId,
					objectType: 'page',
				},
			}).fire();
		} else if (contentType === 'blogpost') {
			dialogs.showModal(MoveBlogModalLoader, {
				currentSpaceKey: spaceKey,
				blogPostId: contentId,
				isEditMode: true,
				isUnpublishedDraft,
				retrieveEditorTitleAndContent,
				referrer: 'editor-move-page-button',
			});
			createAnalyticsEvent({
				type: 'sendUIEvent',
				data: {
					action: 'clicked',
					actionSubject: 'button',
					actionSubjectId: 'openEditorMovePageDialog',
					source: analyticsSource,
					objectId: contentId,
					objectType: 'blog',
				},
			}).fire();
		}
	};

	let component;
	// if FF is on we should show move button for both blog and page types and for both publish and unpublish states
	if (canMoveContent || (isUnpublishedDraft && contentType === 'page')) {
		if (appearance === 'iconButton') {
			const icon = (
				// eslint-disable-next-line @atlaskit/ui-styling-standard/no-classname-prop -- Ignored via go/DSP-18766
				<span className="icon aui-icon aui-icon-small aui-iconfont-nav-children" />
			);

			component = (
				<AkTooltip tag="span" content={intl.formatMessage(i18n.movePageBtnDesc)}>
					<AkButton
						testId="move-page-icon-button"
						iconBefore={icon}
						appearance="subtle"
						onClick={openMoveModal}
					/>
				</AkTooltip>
			);
		} else if (appearance === 'dropdownItem') {
			component = (
				<AkDropdownItem onClick={openMoveModal} testId="move-page-dropdown-item">
					<FormattedMessage {...i18n.movePageBtnDesc} />
				</AkDropdownItem>
			);
		}
	}

	return (
		<div data-testid="move-page-button">
			{error && <ErrorDisplay error={error} />}
			{component}
		</div>
	);
};

export const EditorMovePageButton: React.FC<EditorMovePageButtonComponentProps> = (props) => {
	return (
		<Subscribe to={[DialogsStateContainer]}>
			{(dialogs: DialogsStateContainer) => (
				<EditorMovePageButtonComponentWithDialogs {...props} dialogs={dialogs} />
			)}
		</Subscribe>
	);
};
